<script setup lang="ts" generic="T">
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  interface Group {
    allSelected: boolean;
    heading: string;
    items: T[];
    onSelectAll: () => void;
    showSelectAll: boolean;
  }

  interface Props {
    attachedTooltip: string;
    emptyText: string;
    filterFunction: (list: T[], term: string) => T[];
    getKey: (item: T) => string;
    getLabel: (item: T) => string;
    groups: Group[];
    hasFile: (item: T) => boolean;
    icon: string;
    isAttached: (item: T) => boolean;
    isDisabled: (item: T) => boolean;
    isPending: (item: T) => boolean;
    label: string;
    missingFileTooltip: string;
    placeholder: string;
  }

  defineProps<Props>();

  defineEmits<{
    (e: 'select', item: T): void;
    (e: 'update:search-term', term: string): void;
  }>();
  const open = defineModel<boolean>('open');
  const search = defineModel<string>('search');
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button variant="ghost" role="combobox" size="sm" :aria-expanded="open">
        <i v-if="icon" :class="icon" />
        <span>{{ label }}</span>
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-[250px] p-0" align="start">
      <Command
        :filter-function="filterFunction"
        @update:search-term="(term: string) => $emit('update:search-term', term)"
      >
        <CommandList>
          <div class="relative">
            <CommandInput v-model="search" :placeholder="placeholder" />
          </div>
          <CommandEmpty>{{ emptyText }}</CommandEmpty>
          <div class="h-[200px] overflow-y-auto">
            <CommandGroup v-for="group in groups" :key="group.heading" :heading="group.heading">
              <div
                v-if="group.showSelectAll"
                class="flex flex-row gap-2 cursor-pointer select-none"
                @click="group.onSelectAll"
              >
                <div class="text-xs ml-4 text-right w-full">
                  {{ group.allSelected ? 'Deselect All' : 'Select All' }}
                </div>
                <span class="inline-block w-[16px]">
                  <i v-if="!group.allSelected" class="fa-regular fa-square"></i>
                  <i v-else class="fa-solid fa-square-check text-success"></i>
                </span>
              </div>

              <template v-for="item in group.items" :key="getKey(item)">
                <Tooltip :disabled="hasFile(item) && !isAttached(item)">
                  <TooltipTrigger class="w-full">
                    <CommandItem
                      :value="item"
                      class="flex justify-between w-full text-xs"
                      :disabled="isDisabled(item)"
                      @select="$emit('select', item)"
                    >
                      <div class="flex items-center justify-between w-full space-x-2">
                        <span class="space-x-1.5">
                          <span class="inline-block w-[16px]">
                            <i v-if="isPending(item)" class="fa fa-spinner-third animate-spin" />
                            <i
                              v-else-if="!hasFile(item)"
                              class="fa-solid fa-square text-muted-foreground"
                            />
                            <i
                              v-else-if="isAttached(item)"
                              class="fa-solid fa-square-check text-success"
                            />
                            <i v-else class="fa-regular fa-square" />
                          </span>
                          <strong>{{ getLabel(item) }}</strong>
                        </span>
                        <slot name="item-meta" :item="item" />
                      </div>
                    </CommandItem>
                  </TooltipTrigger>

                  <TooltipContent side="left">
                    <template v-if="isAttached(item)">
                      <i class="fa-solid fa-circle-check text-success mr-0.5" />
                      {{ attachedTooltip }}
                    </template>
                    <template v-else>
                      <i class="fa-solid fa-circle-exclamation text-warning-700 mr-0.5"></i>
                      {{ missingFileTooltip }}
                    </template>
                  </TooltipContent>
                </Tooltip>
              </template>
            </CommandGroup>
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
